'use client'

import { useRef, useState } from 'react'

import { useAnalytics } from '@/hooks/useAnalytics'
import type { ProjectReel as Reel } from '@/lib/projectsData'

function SoundIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="size-4">
      <path d="M3.5 7.5h2.8L10 4.5v11l-3.7-3H3.5v-5Z" fill="currentColor" />
      {on ? (
        <path
          d="M13 7.2a4 4 0 0 1 0 5.6M15.2 5a7 7 0 0 1 0 10"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="m13.2 8 4 4m0-4-4 4"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      )}
    </svg>
  )
}

function PlayPauseIcon({ playing }: { playing: boolean }) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4">
      {playing ? (
        <path d="M6 4.5h2.6v11H6zM11.4 4.5H14v11h-2.6z" fill="currentColor" />
      ) : (
        <path d="M6.5 4.3v11.4L15.5 10 6.5 4.3Z" fill="currentColor" />
      )}
    </svg>
  )
}

const controlClass =
  'flex h-8 items-center gap-1.5 rounded-full bg-zinc-900/60 px-3 text-xs font-medium text-white backdrop-blur-md transition-transform duration-100 ease-out hover:bg-zinc-900/75 active:scale-[0.96]'

// The short animated overview that leads a project page. It autoplays muted
// and loops; turning the sound on restarts it so the score lines up with the
// story from the first beat.
export function ProjectReel({ reel, name }: { reel: Reel; name: string }) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [soundOn, setSoundOn] = useState(false)
  const [playing, setPlaying] = useState(true)
  const { track } = useAnalytics()

  // React sets `muted` as a property after hydration, so the server-rendered
  // autoplay can be refused; retrying once data is ready covers that.
  const handleLoadedData = () => {
    videoRef.current?.play().catch(() => {})
  }

  const toggleSound = () => {
    const video = videoRef.current
    if (!video) return
    const next = !soundOn
    video.muted = !next
    if (next) {
      video.currentTime = 0
      video.play().catch(() => {})
      track('content_engagement', {
        type: 'video_play',
        details: `reel_sound_on:${name}`,
      })
    }
    setSoundOn(next)
  }

  const togglePlaying = () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) video.play().catch(() => {})
    else video.pause()
  }

  return (
    <div
      className="relative mt-10 overflow-hidden rounded-2xl ring-1 ring-zinc-900/5 dark:ring-white/10"
      style={{ backgroundColor: reel.background }}
    >
      <video
        ref={videoRef}
        poster={reel.poster}
        aria-label={`${name} animated overview`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onLoadedData={handleLoadedData}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        className="aspect-video w-full"
      >
        {/* Browsers without an AV1 decoder skip this on the codecs string
            and play the H.264 file. */}
        {reel.av1Src && (
          <source src={reel.av1Src} type='video/mp4; codecs="av01.0.08M.08"' />
        )}
        <source src={reel.src} type="video/mp4" />
      </video>
      <div className="absolute right-3 bottom-3 flex gap-2">
        <button
          type="button"
          onClick={togglePlaying}
          aria-label={playing ? 'Pause overview' : 'Play overview'}
          className={controlClass}
        >
          <PlayPauseIcon playing={playing} />
        </button>
        {reel.hasAudio && (
          <button
            type="button"
            onClick={toggleSound}
            aria-pressed={soundOn}
            aria-label={soundOn ? 'Mute overview' : 'Play overview with sound'}
            className={controlClass}
          >
            <SoundIcon on={soundOn} />
            {soundOn ? 'Sound on' : 'Sound'}
          </button>
        )}
      </div>
    </div>
  )
}
